import cx from 'classnames';
import type { CSSProperties, ElementRef } from 'react';
import { forwardRef, useMemo } from 'react';
import { BoxSelectorContext } from './BoxSelectorContext.js';
import type { BoxSelectorContextProps, BoxSelectorProps } from './types.js';
import { isBoolean } from './utils.js';

export const BoxSelectorRoot = forwardRef(function BoxSelectorRoot<
  T extends 'radio' | 'checkbox' = 'radio',
>(
  {
    onChange,
    onBlur = () => {},
    onFocus = () => {},
    className,
    name,
    value,
    disabled,
    children,
    mktSelector,
    type = 'radio' as T,
    isValid,
    columns,
    label = () => null,
    isTagBox = false,
    description,
    id,
    contentClassName: _contentClassName,
    autoColumns,
    required,
    qaSelector,
    ...wrapperProps
  }: BoxSelectorProps<T>,
  ref: React.Ref<ElementRef<'fieldset'>>,
) {
  const boxId = id ?? name;
  const providerData = useMemo(() => {
    return {
      id: boxId,
      name,
      value,
      type,
      onFocus,
      onBlur,
      onChange,
      disabled,
      isTagBox,
      isValid,
      columns,
      autoColumns,
      label,
      description,
      descriptionId: `${boxId}_description`,
    } satisfies BoxSelectorContextProps<T>;
  }, [
    boxId,
    name,
    value,
    type,
    onFocus,
    onBlur,
    onChange,
    disabled,
    isValid,
    isTagBox,
    label,
    columns,
    autoColumns,
    description,
  ]);
  const qaName = qaSelector ? `${qaSelector}-field` : undefined;
  const parsedColumns = columns ? Math.abs(columns) : null;
  const columnStyle =
    !isTagBox && parsedColumns
      ? ({ '--column-count': parsedColumns } as CSSProperties)
      : {};
  const sharedProps = {
    ...(isBoolean(disabled) ? { disabled } : {}),
  };
  const groupProps = {
    // a `fieldset` is already an implicit group, so only radio needs a role
    ...(type === 'radio' ? { role: 'radiogroup' } : {}),
    ...(isBoolean(required) ? { 'aria-required': required } : {}),
    ...(isBoolean(isValid) ? { 'aria-invalid': !isValid } : {}),
  };
  return (
    <BoxSelectorContext.Provider value={providerData}>
      <fieldset
        ref={ref}
        id={boxId}
        aria-describedby={description ? providerData.descriptionId : undefined}
        className={cx('box-selector', className, {
          'box-selector--grid': !isTagBox,
          'box-selector--tag-box': isTagBox,
        })}
        data-mkt-selector={mktSelector}
        data-qa-selector={qaName}
        style={{ ...columnStyle }}
        {...wrapperProps}
        {...sharedProps}
        {...groupProps}
      >
        {children}
      </fieldset>
    </BoxSelectorContext.Provider>
  );
});

BoxSelectorRoot.displayName = 'BoxSelectorRoot';

// eslint-disable-next-line @typescript-eslint/no-redeclare
export namespace BoxSelectorRoot {
  export interface Props<
    T extends 'radio' | 'checkbox' = 'radio',
  > extends BoxSelectorProps<T> {}
}
